import type { ThemeTypography } from '@echo/tokens';
import { cn } from './cn';

export type AvatarTone =
  | 'green'
  | 'yellow'
  | 'grey'
  | 'olive'
  | 'lavender'
  | 'orange'
  | 'olive-solid';

// Full class names so Tailwind's scanner sees them (never build class names dynamically).
const TONE: Record<AvatarTone, string> = {
  green: 'bg-avatar-green text-on-avatar-green',
  yellow: 'bg-avatar-yellow text-on-avatar-yellow',
  grey: 'bg-avatar-grey text-on-avatar-grey',
  olive: 'bg-avatar-olive text-on-avatar-olive',
  lavender: 'bg-avatar-lavender text-on-avatar-lavender',
  orange: 'bg-avatar-orange text-on-avatar-orange',
  'olive-solid': 'bg-avatar-olive-solid text-on-avatar-olive-solid',
};

/**
 * Figma `avatar`: a circle with the person's photo, or their
 * initials on a soft tint when there is none. `label` names the person for screen readers;
 * the initials alone would be read letter by letter. `size` is in pixels.
 */
export function Avatar({
  src,
  initials,
  tone = 'grey',
  label,
  size = 44,
  className,
  labelLevel,
}: {
  src?: string;
  initials: string;
  tone?: AvatarTone;
  label: string;
  size?: number;
  className?: string;
  /** Optional shared typography level; otherwise initials scale with the avatar size. */
  labelLevel?: ThemeTypography;
}) {
  const shape = { width: size, height: size };
  if (src) {
    return (
      <img
        src={src}
        alt={label}
        style={shape}
        className={cn('rounded-full bg-surface-tertiary object-cover', className)}
      />
    );
  }
  return (
    <span
      role="img"
      aria-label={label}
      style={{
        ...shape,
        fontSize: labelLevel ? `var(--text-${labelLevel})` : Math.round(size / 3),
        lineHeight: labelLevel ? `var(--text-${labelLevel}--line-height)` : undefined,
      }}
      className={cn(
        'inline-flex items-center justify-center rounded-full font-brand font-bold',
        TONE[tone],
        className,
      )}
    >
      {initials}
    </span>
  );
}
